import { Box, Button, Typography } from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CategoriesConfig {
  introduction?: {
    title?: string;
    text?: string;
  };
  key_assignment: {
    randomize: boolean;
    left_hand_keys: string[];
    right_hand_keys: string[];
    "1d_categories": string[];
    "2d_categories": string[];
  };
  phases: Array<{
    phase: number;
    structure: "1D" | "2D" | "mixed";
    rounds: number;
    trials_per_round: number;
    response_time_limit_ms: number;
    isi_ms: number;
    feedback: boolean;
    tip_available_time_ms?: number;
    categories: string[];
    introduction?: {
      title?: string;
      text?: string;
      instructions?: string[];
    };
  }>;
}

export interface CategoriesTrialResult {
  trial_number: number;
  category: string;
  rect_height_px: number;
  door_position_px: number;
  user_response: string | null;
  is_correct: boolean;
  response_time_ms: number;
  timed_out: boolean;
}

export interface CategoriesRoundResult {
  round_number: number;
  correct_count: number;
  error_count: number;
  omission_count: number;
  trials: CategoriesTrialResult[];
}

export interface CategoriesPhaseResult {
  phase: number;
  rounds: CategoriesRoundResult[];
}

export interface CategoriesTaskAnswer {
  key_assignment: Record<string, string>;
  phases: CategoriesPhaseResult[];
}

interface Props {
  onAnswerChange: (taskAnswer?: CategoriesTaskAnswer) => void;
  categoriesConfig?: Partial<CategoriesConfig>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const HEIGHT_MIN = 8;
const HEIGHT_MAX = 538;
const HEIGHT_THRESHOLD = 269;
const HEIGHT_SD = 80;
const DOOR_MIN_PX = 8;
const DOOR_MAX_PX = 478;
const DOOR_THRESHOLD_PX = 239;
const DOOR_CANVAS_WIDTH = 478; // reference width for door_position_px output
const FEEDBACK_MS = 500;

const CYAN = "#00E5FF";
const MAGENTA = "#FF00E5";

const DEFAULT_CONFIG: CategoriesConfig = {
  key_assignment: {
    randomize: true,
    left_hand_keys: ["A", "S"],
    right_hand_keys: ["K", "L"],
    "1d_categories": ["category_a", "category_b"],
    "2d_categories": ["category_c", "category_d"],
  },
  phases: [
    { phase: 1, structure: "1D", rounds: 4, trials_per_round: 50, response_time_limit_ms: 5000, isi_ms: 1000, feedback: true, tip_available_time_ms: 10000, categories: ["A", "B"] },
    { phase: 2, structure: "2D", rounds: 11, trials_per_round: 50, response_time_limit_ms: 5000, isi_ms: 1000, feedback: true, tip_available_time_ms: 10000, categories: ["C", "D"] },
    { phase: 3, structure: "mixed", rounds: 4, trials_per_round: 100, response_time_limit_ms: 3000, isi_ms: 1000, feedback: false, tip_available_time_ms: 10000, categories: ["A", "B", "C", "D"] },
  ],
};

type Screen = "intro" | "phase_intro" | "isi" | "trial" | "feedback" | "round_break" | "done";
type FeedbackType = "correct" | "error" | "omission";

interface Stimulus {
  category: string;
  categoryKey: string;
  rect_height_px: number;
  door_position_px: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const shuffleArray = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const randInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// Truncated normal distribution via Box-Muller (retries until value falls in range)
const normalRandTruncated = (mean: number, sd: number, min: number, max: number): number => {
  for (let i = 0; i < 200; i++) {
    const u1 = Math.max(Math.random(), 1e-10);
    const u2 = Math.random();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const v = Math.round(mean + sd * z);
    if (v >= min && v <= max) return v;
  }
  return Math.round((min + max) / 2); // fallback
};

const categoryToKey = (cat: string): string => `category_${cat.toLowerCase()}`;

const buildKeyAssignment = (cfg: CategoriesConfig["key_assignment"]): Record<string, string> => {
  const leftKeys = cfg.randomize ? shuffleArray([...cfg.left_hand_keys]) : [...cfg.left_hand_keys];
  const rightKeys = cfg.randomize ? shuffleArray([...cfg.right_hand_keys]) : [...cfg.right_hand_keys];
  const cats1D = cfg.randomize ? shuffleArray([...cfg["1d_categories"]]) : [...cfg["1d_categories"]];
  const cats2D = cfg.randomize ? shuffleArray([...cfg["2d_categories"]]) : [...cfg["2d_categories"]];
  const result: Record<string, string> = {};
  // Each sub-structure: one category gets left-hand key, other gets right-hand key
  if (cats1D[0]) result[cats1D[0]] = leftKeys[0] ?? "A";
  if (cats1D[1]) result[cats1D[1]] = rightKeys[0] ?? "K";
  if (cats2D[0]) result[cats2D[0]] = leftKeys[1] ?? "S";
  if (cats2D[1]) result[cats2D[1]] = rightKeys[1] ?? "L";
  return result;
};

const genStimulus = (category: string): Stimulus => {
  let height: number;
  let doorPx: number;
  // Height means: A/D low (~100), B/C high (~430). Door for C/D constrained by threshold.
  switch (category) {
    case "A": // low height, door anywhere
      height = normalRandTruncated(100, HEIGHT_SD, HEIGHT_MIN, HEIGHT_THRESHOLD - 1);
      doorPx = randInt(DOOR_MIN_PX, DOOR_MAX_PX);
      break;
    case "B": // high height, door anywhere
      height = normalRandTruncated(430, HEIGHT_SD, HEIGHT_THRESHOLD, HEIGHT_MAX);
      doorPx = randInt(DOOR_MIN_PX, DOOR_MAX_PX);
      break;
    case "C": // high height, door left
      height = normalRandTruncated(430, HEIGHT_SD, HEIGHT_THRESHOLD, HEIGHT_MAX);
      doorPx = randInt(DOOR_MIN_PX, DOOR_THRESHOLD_PX - 1);
      break;
    case "D": // low height, door right
      height = normalRandTruncated(100, HEIGHT_SD, HEIGHT_MIN, HEIGHT_THRESHOLD - 1);
      doorPx = randInt(DOOR_THRESHOLD_PX, DOOR_MAX_PX);
      break;
    default:
      height = normalRandTruncated(HEIGHT_THRESHOLD, HEIGHT_SD, HEIGHT_MIN, HEIGHT_MAX);
      doorPx = randInt(DOOR_MIN_PX, DOOR_MAX_PX);
  }
  return {
    category,
    categoryKey: categoryToKey(category),
    rect_height_px: height,
    door_position_px: doorPx,
  };
};

const buildTrialSequence = (categories: string[], count: number): string[] => {
  const trialsPerCat = Math.floor(count / categories.length);
  const seq: string[] = [];
  for (const cat of categories) {
    for (let i = 0; i < trialsPerCat; i++) seq.push(cat);
  }
  const remainder = count - seq.length;
  for (let i = 0; i < remainder; i++) seq.push(categories[i % categories.length]);
  return shuffleArray(seq);
};

const resolveConfig = (raw?: Partial<CategoriesConfig>): CategoriesConfig => {
  if (!raw) return DEFAULT_CONFIG;
  return {
    key_assignment: raw.key_assignment ?? DEFAULT_CONFIG.key_assignment,
    phases: (raw.phases && raw.phases.length > 0)
      ? (raw.phases as CategoriesConfig["phases"])
      : DEFAULT_CONFIG.phases,
  };
};

const getPhaseIntroDefaults = (
  keyAssignment: Record<string, string>
): Record<string, { title: string; text: string; instructions?: string[] }> => {
  const key = (cat: string) => keyAssignment[categoryToKey(cat)] ?? "?";
  return {
    "1D": {
      title: "Fase 1",
      text: "Nesta primeira parte da tarefa, classifique os retângulos de acordo com a sua ALTURA, se são ALTOS ou BAIXOS.",
      instructions: [
        `Aperte a tecla ${key("B")} se o retângulo é ALTO.`,
        `Aperte a tecla ${key("A")} se o retângulo for BAIXO.`,
        "Lembre-se de classificar os retângulos somente em relação à sua altura. A posição da linha não importa neste momento.",
      ],
    },
    "2D": {
      title: "Fase 2",
      text: "A partir de agora, classifique os retângulos de acordo com a sua ALTURA e de acordo com a POSIÇÃO DA LINHA em seu interior.",
      instructions: [
        `Aperte a tecla ${key("C")} se o retângulo é ALTO e a linha está à ESQUERDA.`,
        `Aperte a tecla ${key("D")} se o retângulo for BAIXO com a porta à DIREITA.`,
      ],
    },
    "mixed": {
      title: "Fase 3",
      text: "Continue observando atentamente os estímulos e classificando cada um da melhor forma possível.",
    },
  };
};

const CAT_DESCS: Record<string, string> = {
  A: "Baixo",
  B: "Alto",
  C: "Alto + porta à esquerda",
  D: "Baixo + porta à direita",
};

// ─── Component ────────────────────────────────────────────────────────────────

const CategoriesQuestion: React.FC<Props> = ({ onAnswerChange, categoriesConfig }) => {
  const cfg = React.useMemo(() => resolveConfig(categoriesConfig), [categoriesConfig]);

  // Key assignment and color assignment are randomized once on mount
  const [keyAssignment] = React.useState<Record<string, string>>(
    () => buildKeyAssignment(cfg.key_assignment)
  );
  // A/B get one color, C/D get the other — randomized per participant
  const [colorAssignment] = React.useState<{ ab: string; cd: string }>(() =>
    Math.random() < 0.5 ? { ab: CYAN, cd: MAGENTA } : { ab: MAGENTA, cd: CYAN }
  );

  const keyToCategoryKey = React.useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const [catKey, k] of Object.entries(keyAssignment)) {
      map[k.toUpperCase()] = catKey;
    }
    return map;
  }, [keyAssignment]);

  const validKeys = React.useMemo(
    () => new Set(Object.values(keyAssignment).map(k => k.toUpperCase())),
    [keyAssignment]
  );

  // Timers
  const timersRef = React.useRef<number[]>([]);
  const addTimer = (id: number) => timersRef.current.push(id);
  const clearTimers = React.useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  // Task progress (refs — no re-render needed)
  const phaseIdxRef = React.useRef(0);
  const roundIdxRef = React.useRef(0);
  const trialIdxRef = React.useRef(0);
  const trialSeqRef = React.useRef<string[]>([]);
  const phaseResultsRef = React.useRef<CategoriesPhaseResult[]>([]);
  const currentRoundTrialsRef = React.useRef<CategoriesTrialResult[]>([]);
  const roundCountsRef = React.useRef({ correct: 0, error: 0, omission: 0 });
  const trialStartRef = React.useRef(0);
  const handledRef = React.useRef(false);
  const stimulusRef = React.useRef<Stimulus | null>(null);
  const tipHiddenPhaseRef = React.useRef<number>(-1);
  const tipTimerRef = React.useRef<number | null>(null);

  // UI state
  const [screen, setScreen] = React.useState<Screen>("intro");
  const [stimulus, setStimulus] = React.useState<Stimulus | null>(null);
  const [feedbackType, setFeedbackType] = React.useState<FeedbackType | null>(null);
  const [timeLeftPct, setTimeLeftPct] = React.useState(1);
  const [phaseIdx, setPhaseIdx] = React.useState(0);
  const [roundIdx, setRoundIdx] = React.useState(0);
  //const [currentTrialNum, setCurrentTrialNum] = React.useState(1);
  const [roundStats, setRoundStats] = React.useState({ correct: 0, error: 0, omission: 0 });
  const [tipVisible, setTipVisible] = React.useState(true);

  // Dark box top position — measured before paint so height fills to viewport bottom
  const darkBoxRef = React.useRef<HTMLDivElement>(null);
  const [darkBoxTop, setDarkBoxTop] = React.useState(0);
  React.useLayoutEffect(() => {
    const measure = () => {
      if (darkBoxRef.current) setDarkBoxTop(darkBoxRef.current.getBoundingClientRect().top);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [screen]);

  // Stimulus area size — drives bus scale via ResizeObserver
  const stimAreaRef = React.useRef<HTMLDivElement>(null);
  const [stimAreaH, setStimAreaH] = React.useState(HEIGHT_MAX);
  React.useEffect(() => {
    const el = stimAreaRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      setStimAreaH(entries[0].contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [screen]);

  const busScale = Math.min(1, Math.max(0.2, (stimAreaH - 16) / HEIGHT_MAX));

  // Forward ref — avoids stale closures in recursive timer chains
  const startTrialRef = React.useRef<() => void>(() => {});

  const finishTask = React.useCallback(() => {
    clearTimers();
    const answer: CategoriesTaskAnswer = {
      key_assignment: { ...keyAssignment },
      phases: phaseResultsRef.current.map(p => ({
        phase: p.phase,
        rounds: p.rounds.map(r => ({
          round_number: r.round_number,
          correct_count: r.correct_count,
          error_count: r.error_count,
          omission_count: r.omission_count,
          trials: [...r.trials],
        })),
      })),
    };
    setScreen("done");
    onAnswerChange(answer);
  }, [clearTimers, keyAssignment, onAnswerChange]);

  const saveRoundAndBreak = React.useCallback(() => {
    const phaseNum = cfg.phases[phaseIdxRef.current].phase;
    const roundResult: CategoriesRoundResult = {
      round_number: roundIdxRef.current + 1,
      correct_count: roundCountsRef.current.correct,
      error_count: roundCountsRef.current.error,
      omission_count: roundCountsRef.current.omission,
      trials: [...currentRoundTrialsRef.current],
    };
    let phaseResult = phaseResultsRef.current.find(p => p.phase === phaseNum);
    if (!phaseResult) {
      phaseResult = { phase: phaseNum, rounds: [] };
      phaseResultsRef.current = [...phaseResultsRef.current, phaseResult];
    }
    phaseResult.rounds.push(roundResult);
    setRoundStats({ ...roundCountsRef.current });
    setScreen("round_break");
  }, [cfg.phases]);

  const startTrial = React.useCallback(() => {
    clearTimers();
    if (trialIdxRef.current >= trialSeqRef.current.length) {
      saveRoundAndBreak();
      return;
    }
    const phase = cfg.phases[phaseIdxRef.current];
    const category = trialSeqRef.current[trialIdxRef.current];
    const stim = genStimulus(category);
    stimulusRef.current = stim;
    handledRef.current = false;
    //setCurrentTrialNum(trialIdxRef.current + 1);
    setStimulus(stim);
    setFeedbackType(null);
    setTimeLeftPct(1);
    trialStartRef.current = performance.now();
    setScreen("trial");

    if (phase.tip_available_time_ms != null && tipHiddenPhaseRef.current !== phaseIdxRef.current) {
      setTipVisible(true);
      tipTimerRef.current = window.setTimeout(() => {
        tipHiddenPhaseRef.current = phaseIdxRef.current;
        setTipVisible(false);
      }, phase.tip_available_time_ms);
    }

    const barStart = performance.now();
    addTimer(window.setInterval(() => {
      const pct = Math.max(0, 1 - (performance.now() - barStart) / phase.response_time_limit_ms);
      setTimeLeftPct(pct);
    }, 50) as unknown as number);

    addTimer(window.setTimeout(() => {
      if (handledRef.current) return;
      handledRef.current = true;
      clearTimers();
      const s = stimulusRef.current!;
      currentRoundTrialsRef.current.push({
        trial_number: trialIdxRef.current + 1,
        category: s.category,
        rect_height_px: s.rect_height_px,
        door_position_px: s.door_position_px,
        user_response: null,
        is_correct: false,
        response_time_ms: phase.response_time_limit_ms,
        timed_out: true,
      });
      roundCountsRef.current.omission += 1;
      trialIdxRef.current += 1;
      if (phase.feedback) {
        setFeedbackType("omission");
        setScreen("feedback");
        addTimer(window.setTimeout(() => {
          setScreen("isi");
          addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
        }, FEEDBACK_MS));
      } else {
        setScreen("isi");
        addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
      }
    }, phase.response_time_limit_ms));
  }, [cfg.phases, clearTimers, saveRoundAndBreak]);

  startTrialRef.current = startTrial;

  const handleResponse = React.useCallback((key: string) => {
    if (handledRef.current || screen !== "trial") return;
    handledRef.current = true;
    clearTimers();
    const phase = cfg.phases[phaseIdxRef.current];
    const s = stimulusRef.current!;
    const responseMs = Math.round(performance.now() - trialStartRef.current);
    const responseCatKey = keyToCategoryKey[key.toUpperCase()] ?? null;
    const isCorrect = responseCatKey === s.categoryKey;
    currentRoundTrialsRef.current.push({
      trial_number: trialIdxRef.current + 1,
      category: s.category,
      rect_height_px: s.rect_height_px,
      door_position_px: s.door_position_px,
      user_response: responseCatKey,
      is_correct: isCorrect,
      response_time_ms: responseMs,
      timed_out: false,
    });
    if (isCorrect) roundCountsRef.current.correct += 1;
    else roundCountsRef.current.error += 1;
    trialIdxRef.current += 1;
    const fb: FeedbackType = isCorrect ? "correct" : "error";
    if (phase.feedback) {
      setFeedbackType(fb);
      setScreen("feedback");
      addTimer(window.setTimeout(() => {
        setScreen("isi");
        addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
      }, FEEDBACK_MS));
    } else {
      setScreen("isi");
      addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
    }
  }, [cfg.phases, clearTimers, keyToCategoryKey, screen]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const key = e.key.toUpperCase();
      if (validKeys.has(key) && screen === "trial") {
        e.preventDefault();
        handleResponse(key);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleResponse, screen, validKeys]);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  const startRound = React.useCallback((pIdx: number, rIdx: number) => {
    const phase = cfg.phases[pIdx];
    phaseIdxRef.current = pIdx;
    roundIdxRef.current = rIdx;
    trialIdxRef.current = 0;
    currentRoundTrialsRef.current = [];
    roundCountsRef.current = { correct: 0, error: 0, omission: 0 };
    trialSeqRef.current = buildTrialSequence(phase.categories, phase.trials_per_round);
    setPhaseIdx(pIdx);
    setRoundIdx(rIdx);
    setScreen("isi");
    addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
  }, [cfg.phases]);

  const startPhase = React.useCallback((pIdx: number) => {
    if (tipTimerRef.current != null) {
      clearTimeout(tipTimerRef.current);
      tipTimerRef.current = null;
    }
    phaseIdxRef.current = pIdx;
    tipHiddenPhaseRef.current = -1;
    setTipVisible(true);
    setPhaseIdx(pIdx);
    setScreen("phase_intro");
  }, []);

  const onContinueRound = React.useCallback(() => {
    const pIdx = phaseIdxRef.current;
    const nextRound = roundIdxRef.current + 1;
    const phase = cfg.phases[pIdx];
    if (nextRound >= phase.rounds) {
      const nextPhase = pIdx + 1;
      if (nextPhase >= cfg.phases.length) finishTask();
      else startPhase(nextPhase);
    } else {
      startRound(pIdx, nextRound);
    }
  }, [cfg.phases, finishTask, startPhase, startRound]);

  const getPhaseLegend = (pIdx: number): Array<{ cat: string; key: string; desc: string; color: string }> => {
    const phase = cfg.phases[pIdx];
    if (!phase) return [];
    return phase.categories.map(cat => ({
      cat,
      key: keyAssignment[categoryToKey(cat)] ?? "?",
      desc: CAT_DESCS[cat] ?? cat,
      color: (cat === "A" || cat === "B") ? colorAssignment.ab : colorAssignment.cd,
    }));
  };

  const renderLegend = (items: Array<{ cat: string; key: string; desc: string; color: string }>) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 1 }}>
      {items.map(({ cat, key, desc, color }) => (
        <Box
          key={cat}
          sx={{
            background: "#1E1E2E",
            border: `1px solid ${color}44`,
            borderRadius: 2,
            p: "8px 14px",
            textAlign: "center",
            minWidth: 130,
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color }}>
            [{key}] — Categoria {cat}
          </Typography>
          <Typography variant="caption" sx={{ color: "#aaa", mt: 0.25, textTransform: "uppercase" }}>
            {desc}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderBus = (stim: Stimulus) => {
    const busColor = (stim.category === "A" || stim.category === "B")
      ? colorAssignment.ab
      : colorAssignment.cd;
    const doorLeftPct = (stim.door_position_px / DOOR_CANVAS_WIDTH) * 100;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-end", height: "100%", width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            width: { xs: "70%", sm: "60%", md: 480 },
            maxWidth: 480,
            height: `${Math.round(stim.rect_height_px * busScale)}px`,
            border: "2px solid #ffffff",
            borderTop: "none",
          }}
        >
          {/* Roof */}
          <Box
            sx={{
              position: "absolute",
              top: -12,
              left: -2,
              right: -2,
              height: "12px",
              backgroundColor: busColor,
              borderRadius: "3px 3px 0 0",
            }}
          />
          {/* Door line */}
          <Box
            sx={{
              position: "absolute",
              top: 4,
              bottom: 4,
              left: `${doorLeftPct}%`,
              width: "3px",
              backgroundColor: busColor,
              borderRadius: 1,
            }}
          />
        </Box>
      </Box>
    );
  };

  const phase = cfg.phases[Math.min(phaseIdx, cfg.phases.length - 1)];
  const totalRounds = phase?.rounds ?? 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 760,
        boxSizing: "border-box",
        margin: "0 auto",
        mt: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* INTRO */}
      {screen === "intro" && (
        <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
          <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
            {categoriesConfig?.introduction?.title ?? "Tarefa de Aprendizagem de Categorias"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {categoriesConfig?.introduction?.text ?? "Nesta tarefa, você verá imagens de retângulos no centro da tela. Cada retângulo possui uma linha em seu interior. Note que tanto a altura do retângulo quanto o lugar onde a linha aparece podem mudar de uma imagem para outra. Você deve classificá-los pressionando as teclas indicadas."}
          </Typography>
          {renderLegend(
            (["A", "B", "C", "D"] as const).map(cat => ({
              cat,
              key: keyAssignment[categoryToKey(cat)] ?? "?",
              desc: CAT_DESCS[cat],
              color: (cat === "A" || cat === "B") ? colorAssignment.ab : colorAssignment.cd,
            }))
          )}
          <Button
            variant="contained"
            sx={{ mt: 3, borderRadius: 999, px: 4, color: "white" }}
            onClick={() => startPhase(0)}
          >
            Iniciar
          </Button>
        </Box>
      )}

      {/* PHASE INTRO */}
      {screen === "phase_intro" && (() => {
        const currentPhase = cfg.phases[phaseIdx];
        const defaults = getPhaseIntroDefaults(keyAssignment)[currentPhase?.structure] ?? { title: `Fase ${currentPhase?.phase}`, text: "" };
        const intro = currentPhase?.introduction;
        const phaseTitle = intro?.title ?? defaults.title;
        const phaseText = intro?.text ?? defaults.text;
        const phaseInstructions = intro?.instructions ?? defaults.instructions;
        return (
          <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
              {phaseTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {phaseText}
            </Typography>
            {phaseInstructions && phaseInstructions.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  textAlign: "left",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Instruções
                </Typography>
                <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                  {phaseInstructions.map((line: string, i: number) => (
                    <Box component="li" key={i} sx={{ marginBottom: "0.3rem" }}>
                      <Typography variant="body2" color="text.secondary">{line}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {renderLegend(getPhaseLegend(phaseIdx))}
            <Button
              variant="contained"
              sx={{ mt: 3, borderRadius: 999, px: 4, color: "white" }}
              onClick={() => startRound(phaseIdx, 0)}
            >
              Começar
            </Button>
          </Box>
        );
      })()}

      {/* TRIAL / FEEDBACK / ISI */}
      {(screen === "trial" || screen === "feedback" || screen === "isi") && (
        <Box
          ref={darkBoxRef}
          sx={{
            width: "100%",
            backgroundColor: "#08080F",
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            color: "#E8E8F0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            height: darkBoxTop > 0 ? { xs: `calc(100dvh - ${darkBoxTop}px - 2rem)`, md: `calc(100dvh - ${darkBoxTop}px - 3rem)` } : undefined,
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" sx={{ color: "#00E5FF" }}>
              Fase {cfg.phases[phaseIdx]?.phase} · Bloco {roundIdx + 1}/{totalRounds}
            </Typography>
          </Box>

          {/* Time bar */}
          <Box sx={{ width: "100%", height: 4, backgroundColor: "#1A1A25", borderRadius: 2, overflow: "hidden" }}>
            {screen === "trial" && (
              <Box
                sx={{
                  height: "100%",
                  width: `${timeLeftPct * 100}%`,
                  backgroundColor: timeLeftPct > 0.5 ? "#00FF9D" : timeLeftPct > 0.25 ? "#FFD700" : "#FF4444",
                  transition: "width 0.05s linear",
                }}
              />
            )}
          </Box>

          {/* Stimulus area */}
          <Box
            ref={stimAreaRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {screen === "trial" && stimulus && renderBus(stimulus)}
            {screen === "feedback" && (
              feedbackType === "correct" ? (
                <CheckCircleIcon sx={{ fontSize: 64, color: "#00FF9D" }} />
              ) : feedbackType === "omission" ? (
                <HourglassEmptyIcon sx={{ fontSize: 64, color: "#FFD700" }} />
              ) : (
                <CancelIcon sx={{ fontSize: 64, color: "#FF4444" }} />
              )
            )}
            {screen === "isi" && (
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#333" }} />
            )}
          </Box>

          {/* Touch response buttons */}
          {screen === "trial" && (
            <Box sx={{ display: { xs: "flex", md: tipVisible ? "flex" : "none" }, gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              {getPhaseLegend(phaseIdx).map(({ key, color, desc }) => (
                <Button
                  key={key}
                  onClick={() => handleResponse(key)}
                  sx={{
                    p: "10px 14px",
                    minWidth: 80,
                    minHeight: 56,
                    background: "#111118",
                    border: `2px solid ${color}55`,
                    borderRadius: 2,
                    color: "#E8E8F0",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    "&:hover": { background: "#1E1E2E", borderColor: color, border: `2px solid ${color}` },
                  }}
                >
                  <Typography variant="caption" sx={{ display: tipVisible ? "block" : { xs: "block", md: "none" }, color: "#E8E8F0", textAlign: "center", maxWidth: 110, lineHeight: 1.2, textTransform: "none" }}>
                    {tipVisible ? desc : key}
                  </Typography>
                </Button>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ROUND BREAK */}
      {screen === "round_break" && (
        <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
          <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
            Rodada {roundIdx + 1} concluída
          </Typography>
          <Box sx={{ display: "flex", gap: 3, justifyContent: "center", mb: 3 }}>
            {(
              [
                ["Acertos", roundStats.correct, "#43a047"],
                ["Erros", roundStats.error, "#e53935"],
                ["Omissões", roundStats.omission, "#fb8c00"],
              ] as const
            ).map(([label, value, color]) => (
              <Box key={label}>
                <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
                  {value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            sx={{ borderRadius: 999, px: 4, color: "white" }}
            onClick={onContinueRound}
          >
            Continuar
          </Button>
        </Box>
      )}

      {/* DONE */}
      {screen === "done" && (
        <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
          <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
            Tarefa concluída!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Clique em "Próximo" para continuar.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CategoriesQuestion;
